import { Component, signal, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MapComponent, MapMarker } from '../../shared/components/map/map.component';
import { ScrollAnimateDirective } from '../../shared/directives/scroll-animate.directive';
import { SeoService } from '../../core/services/seo.service';
import { SiteConfigService } from '../../core/config/site-config.service';
import { ParticlesComponent } from '../../shared/components/particles/particles.component';
@Component({
  selector: 'app-contact',
  standalone: true,
  imports: [FormsModule, MapComponent, ScrollAnimateDirective, ParticlesComponent],
  template: `
    <!-- Hero -->
    <section class="relative py-24 md:py-32 -mt-20 pt-32 md:pt-40 bg-surface-950 overflow-hidden grain-overlay">
      <div class="absolute inset-0 bg-gradient-to-br from-brand-950 via-surface-950 to-brand-900"></div>
      <app-particles class="absolute inset-0 z-[1] pointer-events-none" [count]="50" [connectionDistance]="130" [mouseRadius]="180" />
      <div class="mx-auto max-w-7xl px-6 lg:px-8 relative z-10">
        <div class="max-w-2xl">
          <div class="section-label section-label-light">{{ page.hero.label }}</div>
          <h1 class="font-heading text-4xl md:text-5xl font-extrabold text-white leading-tight tracking-tight mb-5">
            {{ page.hero.title }} <span class="text-brand-300">{{ page.hero.highlight }}</span>
          </h1>
          <p class="text-brand-200 leading-relaxed">{{ page.hero.description }}</p>
        </div>
      </div>
    </section>

    <!-- Contact -->
    <section class="py-20 md:py-28 bg-white">
      <div class="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div class="grid grid-cols-1 lg:grid-cols-5 gap-14">
          <!-- Info -->
          <div class="lg:col-span-2" appScrollAnimate data-animate="slide-left">
            <h2 class="font-heading text-2xl font-bold text-surface-900 mb-6">Contact Information</h2>
            <div class="space-y-5 mb-8">
              @for (info of contactInfo; track info.label) {
                <div class="flex items-start gap-3.5">
                  <div class="w-10 h-10 rounded-lg bg-brand-50 text-brand-600 flex items-center justify-center shrink-0">
                    <span class="text-lg" [innerHTML]="info.icon"></span>
                  </div>
                  <div>
                    <span class="text-xs font-semibold uppercase tracking-wider text-surface-400 block mb-0.5">{{ info.label }}</span>
                    <span class="text-sm text-surface-700" [innerHTML]="info.value"></span>
                  </div>
                </div>
              }
            </div>

            <!-- Service areas -->
            <div class="p-5 rounded-xl bg-brand-50 border border-brand-100">
              <h3 class="font-heading text-sm font-bold text-brand-800 mb-3">Service Areas</h3>
              <div class="flex flex-wrap gap-2">
                @for (area of contact.serviceAreas; track area) {
                  <span class="px-2.5 py-1 bg-white text-xs font-medium text-brand-700 rounded-md border border-brand-100">{{ area }}</span>
                }
              </div>
            </div>
          </div>

          <!-- Form -->
          <div class="lg:col-span-3" appScrollAnimate data-animate="slide-right">
            <div class="p-7 md:p-9 rounded-xl bg-surface-50 border border-surface-100">
              <h2 class="font-heading text-xl font-bold text-surface-900 mb-6">Request a Free Estimate</h2>

              @if (submitted()) {
                <div class="py-12 text-center">
                  <div class="w-14 h-14 rounded-full bg-green-100 text-green-600 flex items-center justify-center mx-auto mb-4">
                    <svg aria-hidden="true" class="w-7 h-7" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7" /></svg>
                  </div>
                  <h3 class="font-heading text-lg font-bold text-surface-900 mb-2">Request Sent!</h3>
                  <p class="text-sm text-surface-500">We'll contact you within 24 hours to schedule your free estimate.</p>
                </div>
              } @else {
                <form (ngSubmit)="onSubmit()" class="space-y-4" aria-label="Request a free estimate">
                  <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label for="contact-name" class="block text-xs font-semibold text-surface-600 uppercase tracking-wider mb-1.5">Name *</label>
                      <input id="contact-name" type="text" [(ngModel)]="form.name" name="name" required autocomplete="name"
                             class="w-full px-4 py-2.5 bg-white border border-surface-200 rounded-lg text-sm focus:outline-none focus:border-brand-500 focus:ring-1 focus:ring-brand-500/30" />
                    </div>
                    <div>
                      <label for="contact-phone" class="block text-xs font-semibold text-surface-600 uppercase tracking-wider mb-1.5">Phone *</label>
                      <input id="contact-phone" type="tel" [(ngModel)]="form.phone" name="phone" required autocomplete="tel"
                             class="w-full px-4 py-2.5 bg-white border border-surface-200 rounded-lg text-sm focus:outline-none focus:border-brand-500 focus:ring-1 focus:ring-brand-500/30" />
                    </div>
                  </div>
                  <div>
                    <label for="contact-email" class="block text-xs font-semibold text-surface-600 uppercase tracking-wider mb-1.5">Email *</label>
                    <input id="contact-email" type="email" [(ngModel)]="form.email" name="email" required autocomplete="email"
                           class="w-full px-4 py-2.5 bg-white border border-surface-200 rounded-lg text-sm focus:outline-none focus:border-brand-500 focus:ring-1 focus:ring-brand-500/30" />
                  </div>
                  <div>
                    <label for="contact-service" class="block text-xs font-semibold text-surface-600 uppercase tracking-wider mb-1.5">Service Needed</label>
                    <select id="contact-service" [(ngModel)]="form.service" name="service"
                            class="w-full px-4 py-2.5 bg-white border border-surface-200 rounded-lg text-sm focus:outline-none focus:border-brand-500 focus:ring-1 focus:ring-brand-500/30">
                      <option value="">Select a service...</option>
                      @for (svc of page.formServices; track svc.value) {
                        <option [value]="svc.value">{{ svc.label }}</option>
                      }
                    </select>
                  </div>
                  <div>
                    <label for="contact-message" class="block text-xs font-semibold text-surface-600 uppercase tracking-wider mb-1.5">Project Details</label>
                    <textarea id="contact-message" [(ngModel)]="form.message" name="message" rows="4"
                              class="w-full px-4 py-2.5 bg-white border border-surface-200 rounded-lg text-sm focus:outline-none focus:border-brand-500 focus:ring-1 focus:ring-brand-500/30 resize-none" placeholder="Tell us about your project..."></textarea>
                  </div>
                  <button type="submit"
                          class="w-full py-3 bg-brand-600 text-white font-semibold text-sm rounded-lg hover:bg-brand-700 transition-colors">
                    Submit Request
                  </button>
                </form>
              }
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Map -->
    <section class="bg-surface-50 pb-20">
      <div class="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div appScrollAnimate data-animate class="h-[400px] rounded-xl overflow-hidden border border-surface-200 shadow-sm">
          <app-map [lat]="contact.coordinates.lat" [lng]="contact.coordinates.lng" [zoom]="12" [markers]="mapMarkers" />
        </div>
      </div>
    </section>
  `,
})
export class ContactComponent implements OnInit {
  private seo = inject(SeoService);
  private config = inject(SiteConfigService);

  page = this.config.pages.contact;
  contact = this.config.contact;
  brand = this.config.brand;

  ngOnInit(): void {
    this.seo.setJsonLd([
      this.seo.getLocalBusinessSchema(),
      this.seo.getBreadcrumbSchema([
        { name: 'Home', url: '/' },
        { name: 'Contact Us', url: '/contact' },
      ]),
    ]);
  }

  submitted = signal(false);
  form = { name: '', phone: '', email: '', service: '', message: '' };

  contactInfo = [
    { label: 'Address', icon: '&#128205;', value: this.contact.address.full },
    { label: 'Phone', icon: '&#128222;', value: this.contact.phone },
    { label: 'Email', icon: '&#9993;', value: this.contact.email },
    { label: 'Hours', icon: '&#128336;', value: this.contact.hours.map(h => `${h.label}: ${h.value}`).join(' &middot; ') },
  ];

  mapMarkers: MapMarker[] = [
    { lat: this.contact.coordinates.lat, lng: this.contact.coordinates.lng, label: this.brand.name, description: this.contact.address.full },
  ];

  onSubmit() { this.submitted.set(true); }
}
